import { tokenize, unique } from './tokenizer';
import {
  scoreEntry,
  scoreSelectorTokens,
  isEntryInTargetApp,
  getTargetElementSearchText,
  applyRouteSpecificEntryScore,
  extractNestedComponentSelectors,
  scoreInteractiveTargetMatch,
  scoreComponentChainMatch,
  scoreAppReachability,
  isRowWrapLayoutBug,
  isHomeMoviesCarouselBug
} from './scoring';
import { loadSearchIndex, searchResultToEntry, type ScoredSearchResult } from './search';
import type {
  BugfixInput,
  ComponentMap,
  ComponentMapEntry,
  CodeSnippet,
  FixRoutingDecision,
  HypothesisResult,
  ScoringResult
} from './types';

export interface ReasoningAdapters {
  fileReader: (path: string) => string | null;
  pathExists: (path: string) => boolean;
  templateReader: (entry: ComponentMapEntry) => string;
  appStyleBundle?: {
    exposedCssVariables: string[];
    consumedCssVariables?: string[];
    files?: Array<{ path: string; exposedCssVariables: string[]; consumedCssVariables?: string[] }>;
  } | null;
}

const STYLE_SIGNALS = [
  'style',
  'hover',
  'underline',
  'icon',
  'color',
  'spacing',
  'padding',
  'margin',
  'align',
  'alignment',
  'wrap',
  'wrapped',
  'row',
  'line',
  'width',
  'horizontal',
  'stack',
  'visible',
  'visibility',
  'font',
  'border',
  'position',
  'left',
  'right',
  'order',
  'flex',
  'justify'
];

const CONFIG_SIGNALS = [
  'config',
  'configuration',
  'enable',
  'disable',
  'hide',
  'show only',
  'max visible',
  'filter past',
  'orientation',
  'dropdown'
];

const STYLE_VARIABLE_BONUS: Record<string, number> = {
  color: 1,
  label: 2,
  dropdown: 2,
  'load-more': 2,
  icon: 1,
  underline: 1,
  width: 2,
  flex: 2,
  flow: 2,
  wrap: 2,
  row: 1,
  content: 1,
  country: 2,
  'watch-at-home': 2,
  watchathome: 2
};

const COLOR_VARIABLE_TOKENS = ['color', 'background', 'border', 'contrast', 'hover', 'focus', 'active'];

export function findRelevantEntries(
  componentMap: ComponentMap,
  input: BugfixInput,
  adapters: ReasoningAdapters
): ScoringResult['entries'] {
  const primaryTokens = unique([
    ...tokenize(input.scope),
    ...tokenize(input.bugContext),
    ...tokenize(input.expectedResult),
    ...tokenize(input.reviewFeedback || ''),
    ...tokenize(getTargetElementSearchText(input))
  ]);
  const hintTokens = unique(input.likelyFiles.flatMap(tokenize));

  const baseEntries = scoreBaseEntries(componentMap, input, primaryTokens, hintTokens);

  const existingPaths = new Set(baseEntries.map(entry => entry.sourcePath));
  const nestedSelectors = unique(
    baseEntries
      .filter(entry => entry.kind === 'component')
      .flatMap(entry => {
        const preComputed = entry.selectors?.nestedComponents || [];
        if (preComputed.length > 0) {
          return preComputed;
        }
        return extractNestedComponentSelectors(adapters.templateReader(entry));
      })
  );

  const nestedEntries = scoreNestedEntries(componentMap, nestedSelectors, input, primaryTokens, existingPaths);

  return narrowEntriesToRuntimeTarget([...baseEntries, ...nestedEntries], input, adapters).sort((left, right) => {
    if (right.score !== left.score) return right.score - left.score;
    return left.sourcePath.localeCompare(right.sourcePath);
  });
}

function getRuntimeTargetSelectors(input: BugfixInput): string[] {
  const target =
    input.runtimeContext?.target_element ||
    input.reproductionPayload?.target_element ||
    input.reproductionPayload?.runtime_context?.target_element ||
    null;
  if (!target || typeof target !== 'object') {
    return [];
  }

  const values = [
    target.selector,
    ...(Array.isArray(target.selectorCandidates) ? target.selectorCandidates : []),
    ...(Array.isArray(target.componentChain) ? target.componentChain : []),
    target.surface,
    target.structuralOwner?.selector
  ];

  return unique(
    values
      .flatMap(value => String(value || '').match(/\b(?:ush|app|feature|movie-site|user-activity)-[a-z0-9-]+\b/g) || [])
      .filter(Boolean)
  );
}

function narrowEntriesToRuntimeTarget(
  entries: ScoringResult['entries'],
  input: BugfixInput,
  adapters: ReasoningAdapters
): ScoringResult['entries'] {
  const targetSelectors = getRuntimeTargetSelectors(input);
  if (targetSelectors.length === 0) {
    return entries;
  }

  const directMatches = entries.filter(entry => targetSelectors.includes(entry.selector || ''));
  if (directMatches.length === 0) {
    return entries;
  }

  const directPaths = new Set(directMatches.flatMap(entry => entry.relatedPaths || [entry.sourcePath]).filter(Boolean));
  const nestedSelectors = new Set(
    directMatches.flatMap(entry => [
      ...(entry.selectors?.nestedComponents || []),
      ...extractNestedComponentSelectors(adapters.templateReader(entry))
    ])
  );

  const narrowed = entries.filter(entry => {
    if (targetSelectors.includes(entry.selector || '')) return true;
    if (nestedSelectors.has(entry.selector || '')) return true;
    return (entry.relatedPaths || [entry.sourcePath]).some(filePath => directPaths.has(filePath));
  });

  return narrowed.length > 0 ? narrowed : entries;
}

function ensureMiniSearch(componentMap: ComponentMap): void {
  if (!componentMap.miniSearch && componentMap.searchIndex) {
    (componentMap as ComponentMap & { miniSearch: NonNullable<ComponentMap['miniSearch']> }).miniSearch =
      loadSearchIndex(componentMap.searchIndex);
  }
}

function scoreBaseEntries(
  componentMap: ComponentMap,
  input: BugfixInput,
  primaryTokens: string[],
  hintTokens: string[]
): ScoringResult['entries'] {
  ensureMiniSearch(componentMap);

  if (componentMap.miniSearch) {
    const query = [...primaryTokens, ...hintTokens].join(' ');
    const results = componentMap.miniSearch.search(query);

    return results
      .map(result => {
        const stored = componentMap.miniSearch!.getStoredFields(result.id) as unknown as ScoredSearchResult;
        const entry = searchResultToEntry(stored) as unknown as ComponentMapEntry;
        const hintScore = scoreEntry(entry, hintTokens);
        const interactiveScore = scoreInteractiveTargetMatch(entry, input);
        const chainScore = scoreComponentChainMatch(entry, input);
        const reachabilityScore = scoreAppReachability(entry, input.app);
        return {
          ...entry,
          score:
            result.score * 3 +
            hintScore +
            interactiveScore +
            chainScore +
            applyRouteSpecificEntryScore(entry, input) +
            reachabilityScore
        } as ComponentMapEntry & { score: number };
      })
      .filter(entry => isEntryInTargetApp(entry, input.app) && entry.score > 0)
      .sort((left, right) => {
        if (right.score !== left.score) return right.score - left.score;
        return left.sourcePath.localeCompare(right.sourcePath);
      })
      .slice(0, 10);
  }

  return (componentMap.entries || [])
    .filter(entry => isEntryInTargetApp(entry, input.app))
    .map(entry => {
      const primaryScore = scoreEntry(entry, primaryTokens);
      const hintScore = scoreEntry(entry, hintTokens);
      const interactiveScore = scoreInteractiveTargetMatch(entry, input);
      const chainScore = scoreComponentChainMatch(entry, input);
      const reachabilityScore = scoreAppReachability(entry, input.app);
      return {
        ...entry,
        score:
          primaryScore * 3 +
          hintScore +
          interactiveScore +
          chainScore +
          applyRouteSpecificEntryScore(entry, input) +
          reachabilityScore
      };
    })
    .filter(entry => entry.score > 0)
    .sort((left, right) => {
      if (right.score !== left.score) return right.score - left.score;
      return left.sourcePath.localeCompare(right.sourcePath);
    })
    .slice(0, 10);
}

function scoreNestedEntries(
  componentMap: ComponentMap,
  nestedSelectors: string[],
  input: BugfixInput,
  primaryTokens: string[],
  existingPaths: Set<string>
): ScoringResult['entries'] {
  ensureMiniSearch(componentMap);

  if (componentMap.miniSearch) {
    return nestedSelectors
      .flatMap(selector => {
        const results = componentMap.miniSearch!.search(selector, { prefix: false, fuzzy: false });
        return results
          .map(r => componentMap.miniSearch!.getStoredFields(r.id) as unknown as ScoredSearchResult)
          .filter(r => r && r.kind === 'component' && r.selector === selector && !existingPaths.has(r.sourcePath))
          .map(r => searchResultToEntry(r) as unknown as ComponentMapEntry);
      })
      .map(entry => {
        const selectorScore = scoreSelectorTokens(entry.selector || '', primaryTokens);
        const sourceScore = scoreSelectorTokens(entry.sourcePath || '', primaryTokens);
        const reachabilityScore = scoreAppReachability(entry, input.app);
        return {
          ...entry,
          score: Math.max(selectorScore, sourceScore) + applyRouteSpecificEntryScore(entry, input) + reachabilityScore
        } as ComponentMapEntry & { score: number };
      })
      .filter(entry => entry.score > 0 && isEntryInTargetApp(entry, input.app))
      .sort((left, right) => {
        if (right.score !== left.score) return right.score - left.score;
        return left.sourcePath.localeCompare(right.sourcePath);
      })
      .slice(0, 4);
  }

  return nestedSelectors
    .flatMap(selector =>
      (componentMap.entries || []).filter(
        entry =>
          entry.kind === 'component' &&
          entry.selector === selector &&
          !existingPaths.has(entry.sourcePath) &&
          isEntryInTargetApp(entry, input.app)
      )
    )
    .map(entry => {
      const selectorScore = scoreSelectorTokens(entry.selector || '', primaryTokens);
      const sourceScore = scoreSelectorTokens(entry.sourcePath || '', primaryTokens);
      const reachabilityScore = scoreAppReachability(entry, input.app);
      return {
        ...entry,
        score: Math.max(selectorScore, sourceScore) + applyRouteSpecificEntryScore(entry, input) + reachabilityScore
      } as ComponentMapEntry & { score: number };
    })
    .filter(entry => entry.score > 0)
    .sort((left, right) => {
      if (right.score !== left.score) return right.score - left.score;
      return left.sourcePath.localeCompare(right.sourcePath);
    })
    .slice(0, 4);
}

export function buildQueryTokens(input: BugfixInput, relevantEntries: ScoringResult['entries']): string[] {
  const tokens = unique([
    ...tokenize(input.scope),
    ...tokenize(input.bugContext),
    ...tokenize(input.expectedResult),
    ...tokenize(input.reviewFeedback || ''),
    ...relevantEntries.flatMap(entry => tokenize(entry.selector || entry.symbolName || ''))
  ]);

  if (isRowWrapLayoutBug(input)) {
    tokens.push('wrap', 'row', 'line', 'width', 'content', 'flex', 'flow', 'layout', 'social', 'share');
  }

  return tokens;
}

export function extractSnippet(
  filePath: string,
  queryTokens: string[],
  fileReader: (path: string) => string | null,
  maxSnippetLength = 7000
): CodeSnippet | null {
  const content = fileReader(filePath);
  if (content === null) {
    return null;
  }

  const lines = content.split(/\r?\n/);
  const loweredTokens = unique(queryTokens.map(token => token.toLowerCase()));
  const matchingIndexes: number[] = [];

  lines.forEach((line, index) => {
    const lowered = line.toLowerCase();
    if (loweredTokens.some(token => lowered.includes(token))) {
      matchingIndexes.push(index);
    }
  });

  const windows = matchingIndexes.length > 0 ? matchingIndexes.slice(0, 3) : [0];
  const ranges: [number, number][] = [];

  for (const index of windows) {
    const start = Math.max(0, index - 4);
    const end = Math.min(lines.length, index + 5);
    ranges.push([start, end]);
  }

  const merged: [number, number][] = [];
  for (const [start, end] of ranges.sort((left, right) => left[0] - right[0])) {
    const previous = merged[merged.length - 1];
    if (previous && start <= previous[1]) {
      previous[1] = Math.max(previous[1], end);
    } else {
      merged.push([start, end]);
    }
  }

  const out: string[] = [];
  for (const [start, end] of merged) {
    for (let index = start; index < end; index += 1) {
      out.push(`${String(index + 1).padStart(4, ' ')}: ${lines[index]}`);
    }
  }

  return {
    path: filePath,
    snippet: out.join('\n').slice(0, maxSnippetLength)
  };
}

export function buildCodeContext(
  relevantEntries: ScoringResult['entries'],
  input: BugfixInput,
  adapters: ReasoningAdapters
): CodeSnippet[] {
  const queryTokens = unique([
    ...tokenize(input.scope),
    ...tokenize(input.bugContext),
    ...tokenize(input.expectedResult),
    ...tokenize(input.reviewFeedback || ''),
    ...relevantEntries.flatMap(entry => tokenize(entry.selector || entry.symbolName || ''))
  ]);

  if (isRowWrapLayoutBug(input)) {
    queryTokens.push('wrap', 'row', 'line', 'width', 'content', 'flex', 'flow', 'layout', 'social', 'share');
  }

  const includePrimengOverrides = queryTokens.some(token =>
    ['dropdown', 'country', 'label', 'streaming', 'watch', 'home'].includes(token)
  );

  const weightedPaths = new Map<string, number>();
  const addPath = (filePath: string, weight: number) => {
    const normalizedPath = String(filePath || '').trim();
    if (!normalizedPath) return;
    if (!adapters.pathExists(normalizedPath)) return;

    const currentWeight = weightedPaths.get(normalizedPath) || 0;
    if (weight > currentWeight) {
      weightedPaths.set(normalizedPath, weight);
    }
  };

  const scorePathSignal = (filePath: string): number => {
    const normalizedPath = String(filePath || '').toLowerCase();
    let score = tokenize(normalizedPath).reduce((total, token) => total + (queryTokens.includes(token) ? 2 : 0), 0);

    if (isRowWrapLayoutBug(input)) {
      ['wrap', 'row', 'line', 'width', 'content', 'flex', 'flow', 'social', 'share', 'tell', 'friends'].forEach(
        token => {
          if (normalizedPath.includes(token)) score += 3;
        }
      );
    }

    return score;
  };

  addPath(`apps/${input.app}/src/styles/styles.scss`, 120);

  if (includePrimengOverrides) {
    addPath('libs/styles/primeng.overrides.scss', 35);
  }

  relevantEntries.forEach(entry => {
    const baseWeight = 50 + Number(entry?.score || 0) * 12;
    (entry.relatedPaths || [entry.sourcePath]).forEach(filePath => {
      addPath(filePath, baseWeight + scorePathSignal(filePath));
    });
  });

  const paths = [...weightedPaths.entries()]
    .sort((left, right) => {
      if (right[1] !== left[1]) return right[1] - left[1];
      return left[0].localeCompare(right[0]);
    })
    .map(([filePath]) => filePath)
    .slice(0, 14);

  return paths
    .map(filePath => extractSnippet(filePath, queryTokens, adapters.fileReader))
    .filter(Boolean) as CodeSnippet[];
}

/**
 * Builds a structured hypothesis from the reasoning engine's analysis results.
 * Computes a confidence score (0-1) from signal strength and assembles
 * evidence, missing evidence, and a suggested fix. Designed to feed into
 * LLM prompts or CI workflows as a verified, deterministic baseline.
 */
export function buildHypothesis(
  input: BugfixInput,
  routing: FixRoutingDecision,
  relevantEntries: ScoringResult['entries'],
  codeSnippets: CodeSnippet[]
): HypothesisResult {
  const entryCount = relevantEntries.length;
  const hasChainMatch = relevantEntries.some(e => (e.score || 0) >= 1000);
  const hasVariables = routing.existingStyleVariableHints.length > 0;
  const hasInteractiveTarget =
    (input.runtimeContext?.target_element?.selector || input.reproductionPayload?.target_element?.selector) != null;

  let confidence =
    0.1 +
    (entryCount > 0 ? Math.min(entryCount * 0.1, 0.4) : 0) +
    (hasChainMatch ? 0.2 : 0) +
    (hasVariables ? 0.1 : 0) +
    (hasInteractiveTarget ? 0.1 : 0);
  confidence = Math.min(confidence, 0.95);

  const hypothesis = [
    routing.visualIssueType ? `${capitalize(routing.visualIssueType)} issue detected.` : 'Visual issue detected.',
    entryCount > 0
      ? `Top match: ${relevantEntries[0].selector || relevantEntries[0].symbolName} (score: ${relevantEntries[0].score}).`
      : 'No matching components found.',
    routing.routeAction ? `Recommended action: ${routing.routeAction}.` : 'No specific route action determined.'
  ].join(' ');

  const evidence = relevantEntries.slice(0, 3).flatMap(entry => {
    const matchingSnippets = codeSnippets.filter(s => (entry.relatedPaths || [entry.sourcePath]).includes(s.path));
    return matchingSnippets.length > 0
      ? matchingSnippets.map(s => ({
          filePath: s.path,
          selector: entry.selector || '',
          snippet: s.snippet.substring(0, 500),
          reason: `Top-scored ${entry.kind} (score: ${entry.score}), ${routing.visualIssueType || 'visual'} issue`
        }))
      : [
          {
            filePath: entry.sourcePath,
            selector: entry.selector || '',
            snippet: '',
            reason: `Top-scored ${entry.kind} (score: ${entry.score})`
          }
        ];
  });

  const missingEvidence: string[] = [];
  if (!routing.visualIssueType) missingEvidence.push('Could not classify visual issue type');
  if (routing.existingStyleVariableHints.length === 0) missingEvidence.push('No CSS variables found in relevant files');
  if (entryCount === 0) missingEvidence.push('No matching components found');
  if (!hasChainMatch && !hasInteractiveTarget) missingEvidence.push('No target element or component chain captured');

  const suggestedFix: HypothesisResult['suggestedFix'] =
    routing.routeAction !== 'minimal-code-fix'
      ? {
          filePath: evidence[0]?.filePath || `apps/${input.app}/src/styles/styles.scss`,
          changeType: routing.routeAction.includes('override')
            ? 'scss-variable-override'
            : routing.routeAction.includes('config')
              ? 'component-config'
              : 'structural-fix',
          description: routing.routeAction
        }
      : null;

  return { hypothesis, evidence, missingEvidence, confidence, suggestedFix };
}

function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function buildConsumedAndSettableHints(
  relevantEntries: ScoringResult['entries'],
  appStyleBundle: ReasoningAdapters['appStyleBundle']
): FixRoutingDecision['consumedAndSettableHints'] {
  if (!appStyleBundle) return [];
  const settable = new Set(appStyleBundle.exposedCssVariables || []);
  if (settable.size === 0) return [];

  const seen = new Map<string, FixRoutingDecision['consumedAndSettableHints'][number]>();
  const sortedEntries = [...relevantEntries].sort((left, right) => (right.score ?? 0) - (left.score ?? 0));
  const failingEntry = sortedEntries[0];

  for (const entry of sortedEntries) {
    const consumed = entry.consumedCssVariables || [];
    if (consumed.length === 0) continue;
    for (const variable of consumed) {
      if (!settable.has(variable)) continue;
      if (seen.has(variable)) continue;
      const selectorCandidates = unique([
        entry.selector,
        ...(entry.selectors?.classes || []),
        ...(entry.selectors?.interactive || [])
      ]).filter(Boolean) as string[];
      seen.set(variable, {
        variable,
        reason: entry === failingEntry ? 'consumed-by-failing-component' : 'consumed-by-sibling',
        consumerSymbolName: entry.symbolName || entry.sourcePath || '',
        consumerFile: entry.relatedPaths?.[0] || entry.sourcePath,
        sourceFile: (appStyleBundle.files || []).find(f => f.exposedCssVariables.includes(variable))?.path,
        selectorCandidates,
        isAppSettable: true
      });
    }
  }

  return [...seen.values()];
}

function pickDryCssVariableFixHeuristic(
  hints: FixRoutingDecision['consumedAndSettableHints'],
  input: BugfixInput,
  visualIssueType: string
): FixRoutingDecision['dryCssVariableFixHeuristic'] {
  if (hints.length === 0) return null;
  const text = `${input.bugContext} ${input.expectedResult} ${input.reviewFeedback || ''}`.toLowerCase();
  const issueTokens = getVisualIssueRankingTokens(visualIssueType || '').map(t => t.toLowerCase());

  const ranked = hints
    .map(hint => {
      const variable = hint.variable.toLowerCase();
      let matchScore = 0;
      for (const token of issueTokens) {
        if (variable.includes(token)) matchScore += 2;
      }
      for (const word of variable.split('-')) {
        if (text.includes(word) && word.length >= 3) matchScore += 1;
      }
      if (hint.reason === 'consumed-by-failing-component') matchScore += 1;
      return { hint, matchScore };
    })
    .filter(entry => entry.matchScore >= 2)
    .sort((left, right) => right.matchScore - left.matchScore);

  if (ranked.length === 0) return null;
  const winner = ranked[0];
  return {
    variable: winner.hint.variable,
    consumerFile: winner.hint.consumerFile,
    reason: winner.hint.reason,
    matchScore: winner.matchScore
  };
}

export function classifyFixRouting(
  input: BugfixInput,
  relevantEntries: ScoringResult['entries'],
  codeSnippets: CodeSnippet[],
  adapters: ReasoningAdapters & { environmentKeys: string[]; styleFilePaths: string[] }
): FixRoutingDecision {
  const text = `${input.bugContext} ${input.expectedResult} ${input.reviewFeedback || ''}`.toLowerCase();
  const visualIssueType = classifyVisualIssueType(input);
  const isStructuralVisualIssue = ['position', 'alignment', 'spacing', 'size', 'visibility'].includes(visualIssueType);

  const styleScore = STYLE_SIGNALS.reduce((score, token) => score + (text.includes(token) ? 1 : 0), 0);
  const configScore = CONFIG_SIGNALS.reduce((score, token) => score + (text.includes(token) ? 1 : 0), 0);

  const environmentComponentCandidates = inferEnvironmentComponentCandidates(
    input,
    relevantEntries,
    adapters.environmentKeys
  );

  const rankingTokens = unique([
    ...tokenize(input.scope),
    ...tokenize(input.bugContext),
    ...tokenize(input.expectedResult),
    ...tokenize(input.reviewFeedback || ''),
    ...relevantEntries.flatMap(entry => tokenize(entry.selector || entry.symbolName || '')),
    ...relevantEntries.flatMap(entry => tokenize(entry.sourcePath || '')),
    ...getVisualIssueRankingTokens(visualIssueType)
  ]);

  const entryCssVariables = unique(relevantEntries.flatMap(entry => entry.exposedCssVariables || []));
  const consumedAndSettableHints = buildConsumedAndSettableHints(relevantEntries, adapters.appStyleBundle);
  const consumedHintVariables = consumedAndSettableHints.map(hint => hint.variable);

  const existingStyleVariableHintsFromFile = findExistingStyleVariableHints(
    adapters.styleFilePaths,
    rankingTokens,
    adapters.fileReader
  );

  const existingStyleVariableHints = (
    entryCssVariables.length > 0
      ? rankAndFilterStyleVariables(
          [...new Set([...entryCssVariables, ...consumedHintVariables, ...existingStyleVariableHintsFromFile])],
          rankingTokens
        )
      : rankAndFilterStyleVariables(
          [...new Set([...consumedHintVariables, ...existingStyleVariableHintsFromFile])],
          rankingTokens
        )
  )
    .filter(variable => isStyleVariableRelevantToIssue(variable, visualIssueType, input))
    .slice(0, 20);

  const dryCssVariableFixHeuristic = pickDryCssVariableFixHeuristic(consumedAndSettableHints, input, visualIssueType);

  let fixType: FixRoutingDecision['fixType'] = 'code-fix';
  if (styleScore > 0) {
    fixType = 'shared-style-fix';
  } else if (configScore > 0 && environmentComponentCandidates.length > 0) {
    fixType = 'component-config-fix';
  }

  if (configScore > styleScore && environmentComponentCandidates.length > 0) {
    fixType = 'component-config-fix';
  }

  let routeAction = 'minimal-code-fix';
  if (fixType === 'shared-style-fix') {
    if (existingStyleVariableHints.length > 0) {
      routeAction = 'app-override-existing-variable';
    } else if (isStructuralVisualIssue) {
      routeAction = 'structural-style-override';
    } else {
      routeAction = 'shared-variable-plus-app-override';
    }
  } else if (fixType === 'component-config-fix') {
    routeAction = 'configure-environment-component';
  }

  return {
    fixType,
    routeAction,
    visualIssueType,
    componentKey: fixType === 'component-config-fix' ? environmentComponentCandidates[0]?.key || null : null,
    environmentComponentCandidates,
    existingStyleVariableHints,
    consumedAndSettableHints,
    dryCssVariableFixHeuristic,
    layoutConstraintHints: collectLayoutConstraintHints(input, codeSnippets, adapters.fileReader)
  };
}

function inferEnvironmentComponentCandidates(
  input: BugfixInput,
  relevantEntries: ScoringResult['entries'],
  keys: string[]
): Array<{ key: string; score: number }> {
  const tokens = unique([
    ...tokenize(input.scope),
    ...tokenize(input.bugContext),
    ...tokenize(input.expectedResult),
    ...tokenize(input.reviewFeedback || ''),
    ...relevantEntries.flatMap(entry => tokenize(entry.selector || entry.symbolName || '')),
    ...relevantEntries.flatMap(entry => tokenize(entry.sourcePath))
  ]);

  return keys
    .map(key => ({
      key,
      score: tokenize(key).reduce((score, token) => score + (tokens.includes(token) ? 1 : 0), 0)
    }))
    .filter(candidate => candidate.score > 0)
    .sort((left, right) => {
      if (right.score !== left.score) return right.score - left.score;
      return left.key.localeCompare(right.key);
    });
}

function classifyVisualIssueType(input: BugfixInput): string {
  const explicitIssueType = String(input.visualExpectations?.issueType || '')
    .trim()
    .toLowerCase();
  if (
    [
      'typography',
      'visibility',
      'position',
      'alignment',
      'spacing',
      'size',
      'color',
      'interaction',
      'content'
    ].includes(explicitIssueType)
  ) {
    return explicitIssueType;
  }

  const text = [
    input.bugContext,
    input.expectedResult,
    input.reviewFeedback,
    input.runtimeContext?.target_element?.selector,
    ...(input.runtimeContext?.target_element?.relatedOwners || []).flatMap(owner => [
      owner.kind,
      owner.selector,
      owner.tagName,
      ...(owner.classes || [])
    ]),
    input.runtimeContext?.target_element?.structuralOwner?.selector,
    input.runtimeContext?.target_element?.structuralOwner?.overlayScope,
    input.runtimeContext?.target_element?.structuralOwner?.iconSelector,
    input.runtimeContext?.target_element?.structuralOwner?.inputSelector,
    input.runtimeContext?.target_element?.structuralOwner?.tagName,
    ...(input.runtimeContext?.target_element?.structuralOwner?.classes || []),
    input.runtimeContext?.target_element?.surface,
    input.runtimeContext?.target_element?.role,
    input.runtimeContext?.target_element?.label,
    input.runtimeContext?.target_element?.text,
    ...(input.stepsToReproduce || [])
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  if (/\b(font|font-size|font-family|typography|typeface|text style)\b/.test(text)) return 'typography';
  if (/\b(colou?r|background|contrast|readable)\b/.test(text)) return 'color';
  if (
    /\b(not visible|hidden|invisible|overlap|overlapping|overlaps|covered|covering|occluded|behind|under)\b/.test(text)
  ) {
    return 'visibility';
  }
  if (/\b(left|right|position|icon position|before|after)\b/.test(text)) return 'position';
  if (/\b(alignment|align|misaligned|centered|baseline)\b/.test(text)) return 'alignment';
  if (/\b(spacing|padding|margin|gap|too close|line gap|separation|layout)\b|::after|::before/.test(text)) {
    return 'spacing';
  }
  if (/\b(width|height|size|too big|too small)\b/.test(text)) return 'size';
  if (/\b(click|hover|focus|active|open|close|select|change)\b/.test(text)) return 'interaction';
  if (/\b(text|copy|label|title|content|translation)\b/.test(text)) return 'content';
  return '';
}

function getVisualIssueRankingTokens(issueType: string): string[] {
  switch (issueType) {
    case 'color':
      return ['color', 'background', 'border', 'contrast', 'hover', 'focus', 'active', 'label', 'icon', 'text'];
    case 'position':
      return ['position', 'left', 'right', 'icon', 'input', 'field', 'filter', 'order', 'padding'];
    case 'alignment':
      return ['align', 'alignment', 'justify', 'center', 'baseline', 'flex', 'position'];
    case 'spacing':
      return ['spacing', 'padding', 'margin', 'gap', 'layout', 'offset'];
    case 'size':
      return ['width', 'height', 'size', 'min', 'max'];
    case 'visibility':
      return ['display', 'visibility', 'opacity', 'z-index', 'position'];
    case 'typography':
      return ['font', 'text', 'line-height', 'weight', 'label', 'color'];
    default:
      return [];
  }
}

function isStyleVariableRelevantToIssue(variable: string, issueType: string, input: BugfixInput): boolean {
  const normalized = String(variable || '').toLowerCase();
  const variableTokens = tokenize(String(variable || ''));
  const hasToken = (tokens: string[]) =>
    tokens.some(token => variableTokens.includes(token) || normalized.includes(token));
  if (!issueType) return true;

  if (issueType === 'position') {
    const issueText = `${input.bugContext || ''} ${input.expectedResult || ''}`.toLowerCase();
    const positionalMatch = hasToken([
      'position',
      'left',
      'right',
      'order',
      'float',
      'direction',
      'justify',
      'align',
      'alignment',
      'flex',
      'display',
      'padding',
      'margin',
      'width'
    ]);
    if (/\bicon\b/.test(issueText)) {
      const targetTokens = tokenize(getTargetElementSearchText(input));
      const componentSpecificTokens = [
        'autocomplete',
        'cinema',
        'country',
        'dropdown',
        'header',
        'location',
        'logo',
        'movie',
        'multiselect',
        'search',
        'select',
        'showtime',
        'showtimes',
        'streaming'
      ];
      const variableSpecificTokens = variableTokens.filter(token => componentSpecificTokens.includes(token));
      if (variableSpecificTokens.length > 0 && !variableSpecificTokens.some(token => targetTokens.includes(token))) {
        return false;
      }
      return (variableTokens.includes('icon') || normalized.includes('icon')) && positionalMatch;
    }
    return positionalMatch;
  }

  if (issueType === 'alignment') {
    return hasToken([
      'align',
      'alignment',
      'justify',
      'position',
      'flex',
      'direction',
      'order',
      'left',
      'right',
      'padding',
      'margin',
      'width',
      'display'
    ]);
  }

  if (issueType === 'spacing') {
    return hasToken(['padding', 'margin', 'gap', 'spacing', 'offset']);
  }

  if (issueType === 'size') {
    return hasToken(['width', 'height', 'size', 'min', 'max']);
  }

  if (issueType === 'visibility') {
    return hasToken(['display', 'visibility', 'opacity', 'z', 'index', 'position']);
  }

  if (issueType === 'color') {
    return hasToken(COLOR_VARIABLE_TOKENS);
  }

  if (issueType === 'typography') {
    return hasToken(['font', 'text', 'label', 'line', 'height', 'weight', 'color']);
  }

  return true;
}

function scoreStyleVariableHint(variable: string, tokens: string[]): number {
  const normalized = String(variable || '').toLowerCase();
  let score = 0;

  tokens.forEach(token => {
    if (normalized.includes(token)) {
      score += token.length >= 6 ? 3 : 2;
    }
  });

  Object.entries(STYLE_VARIABLE_BONUS).forEach(([key, bonus]) => {
    if (normalized.includes(key)) score += bonus;
  });

  return score;
}

function rankAndFilterStyleVariables(variables: string[], tokens: string[]): string[] {
  const ranked = variables
    .map(variable => ({
      variable,
      score: scoreStyleVariableHint(variable, tokens)
    }))
    .filter(entry => entry.score > 0)
    .sort((left, right) => {
      if (right.score !== left.score) return right.score - left.score;
      return left.variable.localeCompare(right.variable);
    })
    .map(entry => entry.variable);

  return ranked;
}

function findExistingStyleVariableHints(
  paths: string[],
  rankingTokens: string[],
  fileReader: (path: string) => string | null
): string[] {
  const variables: string[] = [];

  for (const filePath of unique(paths)) {
    const content = fileReader(filePath);
    if (content === null) continue;

    const matches = content.match(/--[A-Za-z0-9-_]+/g) || [];
    variables.push(...matches);
  }

  return rankAndFilterStyleVariables(unique(variables), rankingTokens);
}

function collectLayoutConstraintHints(
  input: BugfixInput,
  codeSnippets: CodeSnippet[],
  fileReader: (path: string) => string | null
): string[] {
  if (!isRowWrapLayoutBug(input)) {
    return [];
  }

  const hints: string[] = [];

  for (const snippetEntry of codeSnippets || []) {
    const snippetPath = String(snippetEntry?.path || '').trim();
    const source = snippetEntry?.snippet || '';
    if (!source.trim()) continue;

    const wrapBlock = findScssBlockByMarker(source, '.wrap');
    if (wrapBlock) {
      hints.push(`wrap block found in ${snippetPath}`);
    }

    const flexSignals = [
      'display: flex',
      'flex-wrap',
      'flex-flow',
      'flex-direction',
      'flex-basis',
      'flex-grow',
      'flex-shrink'
    ];
    flexSignals.forEach(signal => {
      if (source.includes(signal)) {
        hints.push(`${signal} found in ${snippetPath}`);
      }
    });

    const widthSignals = ['min-width', 'max-width', 'width:', 'min-content', 'max-content', 'fit-content'];
    widthSignals.forEach(signal => {
      if (source.includes(signal)) {
        hints.push(`${signal} found in ${snippetPath}`);
      }
    });
  }

  return unique(hints);
}

function findScssBlockByMarker(source: string, marker: string): string | null {
  const text = String(source || '');
  const markerIndex = text.indexOf(String(marker || ''));
  if (markerIndex === -1) return null;

  const openBraceIndex = text.indexOf('{', markerIndex + String(marker || '').length);
  if (openBraceIndex === -1) return null;

  const closeBraceIndex = findMatchingBraceIndex(text, openBraceIndex);
  if (closeBraceIndex === -1) return null;

  return text.slice(markerIndex, closeBraceIndex + 1);
}

function findMatchingBraceIndex(source: string, openBraceIndex: number): number {
  const text = String(source || '');
  if (openBraceIndex < 0 || text[openBraceIndex] !== '{') return -1;

  let depth = 0;
  let inString = false;
  let stringQuote = '';
  let inLineComment = false;
  let inBlockComment = false;

  for (let index = openBraceIndex; index < text.length; index += 1) {
    const char = text[index];
    const nextChar = text[index + 1];
    const prevChar = text[index - 1];

    if (inLineComment) {
      if (char === '\n') inLineComment = false;
      continue;
    }

    if (inBlockComment) {
      if (prevChar === '*' && char === '/') inBlockComment = false;
      continue;
    }

    if (inString) {
      if (char === stringQuote && prevChar !== '\\') {
        inString = false;
        stringQuote = '';
      }
      continue;
    }

    if (char === '/' && nextChar === '/') {
      inLineComment = true;
      index += 1;
      continue;
    }

    if (char === '/' && nextChar === '*') {
      inBlockComment = true;
      index += 1;
      continue;
    }

    if (char === '"' || char === "'" || char === '`') {
      inString = true;
      stringQuote = char;
      continue;
    }

    if (char === '{') {
      depth += 1;
      continue;
    }

    if (char === '}') {
      depth -= 1;
      if (depth === 0) return index;
    }
  }

  return -1;
}
